import { labelRefeicao, type NutrientesTotais, type RefeicaoFicha } from '../../lib/fichaTecnicaPacote'
import { formatNumber } from '../../lib/utils'

const WEEKDAY_LONG = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
]

export type CardapioDiaItem = {
  refeicao: RefeicaoFicha
  descricao: string
  pratoNome?: string | null
}

type DiaLetivoUi = {
  data: string
  trimestre: number | null
  semana_cardapio: number | null
  dia_semana: number
  apto: boolean
}

type Cell = { day: number | null; iso?: string; letivo?: DiaLetivoUi }

type Props = {
  mesLabel: string
  grupoLabel: string
  turnoLabel: string
  nutricionistaLabel: string
  cells: Cell[]
  diasComCardapio: Set<string>
  selectedDates: string[]
  focusedDate: string | null
  cardapiosDia: CardapioDiaItem[]
  loadingCardapioDia: boolean
  totaisPorSecao: Map<RefeicaoFicha, NutrientesTotais>
  writableCardapio: boolean
  applying: boolean
  selectedSlotsCount: number
  onPrevMonth: () => void
  onNextMonth: () => void
  onFocusDia: (iso: string) => void
  onToggleFocusedInBatch: (checked: boolean) => void
  onSelecionarTodosLetivos: () => void
  onLimparSelecao: () => void
  onOpenApply: (modo: 'dia' | 'lote') => void
  onClearDay: () => void
  onClearLote: () => void
  onRemoveRefeicaoDia: (refeicao: RefeicaoFicha) => void
}

function formatarDataPainel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const meses = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ]
  return `${d} de ${meses[m - 1]} de ${y}`
}

export function FichaCalendarioDetalhes(props: Props) {
  const {
    mesLabel,
    grupoLabel,
    turnoLabel,
    nutricionistaLabel,
    cells,
    diasComCardapio,
    selectedDates,
    focusedDate,
    cardapiosDia,
    loadingCardapioDia,
    totaisPorSecao,
    writableCardapio,
    applying,
    selectedSlotsCount,
    onPrevMonth,
    onNextMonth,
    onFocusDia,
    onToggleFocusedInBatch,
    onSelecionarTodosLetivos,
    onLimparSelecao,
    onOpenApply,
    onClearDay,
    onClearLote,
    onRemoveRefeicaoDia,
  } = props

  const focusedLetivo = focusedDate ? cells.find((c) => c.iso === focusedDate)?.letivo : undefined

  let totalDia = { energia_kcal: 0, proteinas_g: 0, carboidratos_g: 0, lipideos_g: 0 }
  for (const item of cardapiosDia) {
    const sub = totaisPorSecao.get(item.refeicao)
    if (sub) {
      totalDia.energia_kcal += sub.energia_kcal
      totalDia.proteinas_g += sub.proteinas_g
      totalDia.carboidratos_g += sub.carboidratos_g
      totalDia.lipideos_g += sub.lipideos_g
    }
  }

  return (
    <>
      <div className="ficha-scope-callout">
        <strong>Cardápio do dia</strong> (tabela <code>cardapios</code>, por trimestre/semana/dia) é independente da{' '}
        <strong>ficha técnica</strong> (template por modalidade × refeição). Aplique só nos dias selecionados; escolha
        as refeições no modal. Totais do pacote × meta ficam na aba <strong>Ficha / Pacote</strong>.
      </div>

      <div className="filters" style={{ marginBottom: 12 }}>
        <div className="field" style={{ marginLeft: 'auto', alignSelf: 'end' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onSelecionarTodosLetivos}>
              Marcar todos letivos
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onLimparSelecao}>
              Limpar seleção
            </button>
          </div>
        </div>
      </div>

      <div className="ficha-calendar-and-details">
        <div className="calendar-grid-card">
          <div className="calendar-toolbar">
            <button type="button" className="calendar-nav-btn" onClick={onPrevMonth} aria-label="Mês anterior">
              ◀
            </button>
            <strong className="calendar-month-title">{mesLabel}</strong>
            <button type="button" className="calendar-nav-btn" onClick={onNextMonth} aria-label="Próximo mês">
              ▶
            </button>
          </div>

          <p className="kpi-sub" style={{ marginBottom: 8 }}>
            Clique em um dia letivo para focar o painel e alternar a seleção em lote.
          </p>

          <div className="calendar-weekdays">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((w) => (
              <div key={w} className="calendar-weekday">
                {w}
              </div>
            ))}
          </div>
          <div className="calendar-grid">
            {cells.map((c, idx) => {
              if (c.day == null) return <div key={`e-${idx}`} className="calendar-cell empty" />
              const letivo = c.letivo
              const selected = c.iso ? selectedDates.includes(c.iso) : false
              const hasCard = c.iso ? diasComCardapio.has(c.iso) : false
              const focused = c.iso === focusedDate
              return (
                <button
                  key={c.iso}
                  type="button"
                  className={[
                    'calendar-cell',
                    'cardapio-day-cell',
                    letivo ? 'letivo' : 'nao-letivo',
                    selected ? 'selected' : '',
                    hasCard ? 'has-items' : '',
                    letivo && !letivo.apto ? 'inapto' : '',
                    focused ? 'focused-day' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={!letivo}
                  onClick={() => c.iso && onFocusDia(c.iso)}
                  title={
                    letivo
                      ? letivo.apto
                        ? `${c.iso} · T${letivo.trimestre} S${letivo.semana_cardapio}`
                        : `${c.iso} · sem T/S`
                      : 'Não letivo'
                  }
                >
                  <span className="calendar-day-num">{c.day}</span>
                  {letivo?.apto && (
                    <span className="cell-tag">
                      T{letivo.trimestre} S{letivo.semana_cardapio}
                    </span>
                  )}
                  {hasCard && <span className="cardapio-day-dot" aria-hidden />}
                </button>
              )
            })}
          </div>

          <div className="cardapio-day-legend kpi-sub">
            <span>
              Selecionados: <strong>{selectedDates.length}</strong>
            </span>
            <span>
              Slots T/S: <strong>{selectedSlotsCount}</strong>
            </span>
            <span className="cardapio-legend-dot">● com cardápio</span>
          </div>

          {writableCardapio && (
            <div className="ficha-batch-apply-bar">
              <div className="ficha-batch-stat">
                Nutricionista RT: <strong>{nutricionistaLabel || '—'}</strong>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                disabled={applying || selectedDates.length === 0}
                onClick={() => onOpenApply('lote')}
              >
                Aplicar pacote nos dias selecionados
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={applying || selectedDates.length === 0}
                onClick={onClearLote}
              >
                Limpar cardápio dos dias selecionados
              </button>
            </div>
          )}
        </div>

        <div className="ficha-day-details-panel">
          <div className="ficha-day-details-header">
            {focusedLetivo?.apto ? (
              <span className="ficha-day-details-badge">
                T{focusedLetivo.trimestre} · S{focusedLetivo.semana_cardapio} · Dia letivo
              </span>
            ) : (
              <span className="ficha-day-details-badge">Sem slot T/S</span>
            )}
            <h3>{focusedDate ? formatarDataPainel(focusedDate) : 'Selecione um dia'}</h3>
            <p className="kpi-sub">
              {focusedDate
                ? `${WEEKDAY_LONG[new Date(`${focusedDate}T12:00:00`).getDay()]} · ${grupoLabel} · ${turnoLabel}`
                : `${grupoLabel} · ${turnoLabel}`}
            </p>
            <div className="ficha-day-details-title-tag">Cardápio do dia</div>
          </div>

          <div className="ficha-day-details-body">
            {loadingCardapioDia ? (
              <p className="kpi-sub">Carregando cardápio do dia…</p>
            ) : !focusedDate || !focusedLetivo?.apto ? (
              <p className="kpi-sub">Selecione um dia letivo com trimestre/semana definidos no calendário.</p>
            ) : cardapiosDia.length === 0 ? (
              <div
                style={{
                  background: '#f6f8f5',
                  border: '1.5px dashed var(--color-border)',
                  borderRadius: 8,
                  padding: 20,
                  textAlign: 'center',
                }}
              >
                <h4 style={{ fontSize: 14, margin: '0 0 8px' }}>Nenhum cardápio gravado</h4>
                <p className="kpi-sub" style={{ marginBottom: 12 }}>
                  Este dia ainda não possui linhas em <code>cardapios</code> para o turno/modalidade atuais.
                </p>
                {writableCardapio && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => onOpenApply('dia')}>
                    Aplicar pacote neste dia
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="kpi-sub" style={{ marginTop: 0 }}>
                  Refeições gravadas neste slot ({cardapiosDia.length})
                </p>
                {cardapiosDia.map((item) => {
                  const sub = totaisPorSecao.get(item.refeicao)
                  return (
                    <div key={item.refeicao} className="ficha-meal-card">
                      <div className="ficha-meal-card-header">
                        <strong>{labelRefeicao(item.refeicao)}</strong>
                        {sub && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#1b5e20' }}>
                            {formatNumber(sub.energia_kcal, 0)} kcal
                          </span>
                        )}
                      </div>
                      {item.pratoNome && (
                        <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 13 }}>
                          Prato: {item.pratoNome}
                        </p>
                      )}
                      <p className="kpi-sub" style={{ margin: '0 0 8px' }}>
                        {item.descricao || '—'}
                      </p>
                      {writableCardapio && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={applying}
                          onClick={() => onRemoveRefeicaoDia(item.refeicao)}
                        >
                          Remover deste dia
                        </button>
                      )}
                    </div>
                  )
                })}
                <div
                  style={{
                    background: '#e8f5e9',
                    border: '1px solid #a5d6a7',
                    borderRadius: 8,
                    padding: 10,
                    fontSize: 12,
                  }}
                >
                  <strong>Total nutricional (refeições neste dia):</strong>
                  <br />
                  {formatNumber(totalDia.energia_kcal, 1)} kcal · PTN {formatNumber(totalDia.proteinas_g, 1)}g · CHO{' '}
                  {formatNumber(totalDia.carboidratos_g, 1)}g · LIP {formatNumber(totalDia.lipideos_g, 1)}g
                </div>
              </>
            )}
          </div>

          {writableCardapio && focusedDate && focusedLetivo?.apto && (
            <div className="ficha-day-details-actions">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedDates.includes(focusedDate)}
                  onChange={(e) => onToggleFocusedInBatch(e.target.checked)}
                />
                Incluir este dia na seleção em lote
              </label>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={applying}
                onClick={() => onOpenApply('dia')}
              >
                Aplicar pacote neste dia
              </button>
              {cardapiosDia.length > 0 && (
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  disabled={applying}
                  onClick={onClearDay}
                >
                  Excluir cardápio deste dia
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
